import React from 'react';
import { Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { payRateModStyles } from '../ModsComponents/PayRate/PayRateMod.styles';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  cancellationAmount,
  cancellationFactor,
  isPerDiem,
  showUpAmount,
  showUpFactor,
} from 'app/models/Placement/PlacementMods';
import { validDecimal } from 'app/helpers/numberHelper';

export const PerDiemRates = ({ isExpanded, isModEdit }: { isExpanded: boolean; isModEdit: boolean }) => {
  const { t } = useTranslation();
  const { classes } = payRateModStyles({ isExpanded });
  const { control, getValues, setValue } = useFormContext();
  const isPerDiemValue = useWatch<any>({ name: isPerDiem });
  const showUpAmountValue = useWatch<any>({ name: showUpAmount });
  const showUpFactorValue = useWatch<any>({ name: showUpFactor });
  const cancellationAmountValue = useWatch<any>({ name: cancellationAmount });
  const cancellationFactorValue = useWatch<any>({ name: cancellationFactor });

  const isReadOnly = !isModEdit || !isPerDiemValue;

  const handleChange = (fieldName: string, value: string, onChange: (v: any) => void) => {
    const validInput = value.replace(/^\./, '0.');
    if (validInput === '' || validDecimal(validInput, { numberLength: 7, decimalLength: 4 })) {
      const valueToSet = validInput === '' || validInput.endsWith('.') ? validInput : parseFloat(validInput);
      setValue(fieldName, valueToSet);
      onChange(valueToSet);
    }
  };

  return (
    <Grid style={{ marginTop: '24px' }}>
      <div>
        <Typography component="span" className={classes.headings}>{`${t(
          'placement.placementMods.perDiem',
        )}`}</Typography>
      </div>
      <Grid className={classes.flex}>
        <Grid className={classes.firstAdditionalField}>
          <Controller
            name={showUpAmount}
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextField
                value={showUpAmountValue ?? ''}
                variant="filled"
                className={classes.additionalTextfields}
                label={`${t(`placement.placementMods.${showUpAmount}`)}`}
                onChange={e => handleChange(showUpAmount, e.target.value, onChange)}
                InputLabelProps={{ shrink: getValues(showUpAmount) ?? false }}
                InputProps={{
                  readOnly: isReadOnly,
                  style: { pointerEvents: isReadOnly ? 'none' : 'auto' },
                  startAdornment: (
                    <>
                      {getValues(showUpAmount) && (
                        <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                      )}
                    </>
                  ),
                  sx: { fontSize: '14px' },
                }}
                {...rest}
              />
            )}
          />
        </Grid>
        <Grid className={classes.factorMargin}>
          <Controller
            name={showUpFactor}
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextField
                value={showUpFactorValue ?? ''}
                variant="filled"
                className={classes.additionalTextfields}
                label={`${t(`placement.placementMods.${showUpFactor}`)}`}
                onChange={e => handleChange(showUpFactor, e.target.value, onChange)}
                InputLabelProps={{ shrink: getValues(showUpFactor) ?? false }}
                InputProps={{
                  readOnly: isReadOnly,
                  style: { pointerEvents: isReadOnly ? 'none' : 'auto' },
                  sx: { fontSize: '14px' },
                }}
                {...rest}
              />
            )}
          />
        </Grid>
        <Grid className={classes.factorMargin}>
          <Controller
            name={cancellationAmount}
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextField
                value={cancellationAmountValue ?? ''}
                variant="filled"
                className={classes.additionalTextfields}
                label={`${t(`placement.placementMods.${cancellationAmount}`)}`}
                onChange={e => handleChange(cancellationAmount, e.target.value, onChange)}
                InputLabelProps={{ shrink: getValues(cancellationAmount) ?? false }}
                InputProps={{
                  readOnly: isReadOnly,
                  style: { pointerEvents: isReadOnly ? 'none' : 'auto' },
                  startAdornment: (
                    <>
                      {getValues(cancellationAmount) && (
                        <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                      )}
                    </>
                  ),
                  sx: { fontSize: '14px' },
                }}
                {...rest}
              />
            )}
          />
        </Grid>
        <Grid className={classes.factorMargin}>
          <Controller
            name={cancellationFactor}
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextField
                value={cancellationFactorValue ?? ''}
                variant="filled"
                className={classes.additionalTextfields}
                label={`${t(`placement.placementMods.${cancellationFactor}`)}`}
                onChange={e => handleChange(cancellationFactor, e.target.value, onChange)}
                InputLabelProps={{ shrink: getValues(cancellationFactor) ?? false }}
                InputProps={{
                  readOnly: isReadOnly,
                  style: { pointerEvents: isReadOnly ? 'none' : 'auto' },
                  sx: { fontSize: '14px' },
                }}
                {...rest}
              />
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
